import { NgZone } from '@angular/core';
import { Device, DeviceSyncStatus } from '@era/locations-sdk';
import { CategoryStoreActions, DeviceStoreActions, LocationsRouterStoreSelectors } from '@era/stores';
import { Store } from '@ngrx/store';
import { RouteGroupRule } from '@rocksoft/shared';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { DeviceSyncStatusActions } from '../sub-features/device-sync/device-sync-statuses-store';

interface RoomStatusEvent {
  locationId: string;
  roomId: string;
  status: 'CheckedIn' | 'CheckedOut';
}

/**
 * Creates route group rules for the Locations hub
 */
export function createLocationsRules(store: Store, zone: NgZone): RouteGroupRule[] {
  return [
    // Devices route - handles device changes and sync status
    {
      name: 'devices-location-group',
      urlPattern: /^\/devices\/brands\/[^/]+\/locations\/[^/]+/, // (/devices/brands/:brandId/locations/:locationId)
      hub: 'locations',
      groupName: async (store) => {
        const location = await lastValueFrom(store.select(LocationsRouterStoreSelectors.selectLocation).pipe(take(1)));
        if (!location?.uniqueId) return null;
        return `location_${location.uniqueId}_devices`;
      },
      eventHandlers: [
        {
          eventName: 'OnDeviceChanged',
          handler: (device: Device) => {
            zone.run(() => {
              store.dispatch(
                DeviceStoreActions.DeviceSignalRHubActions.deviceChanged({
                  device: {
                    ...device,
                    lastSeen: device.lastSeen ?? undefined,
                    id: device.id ?? '',
                    deviceId: device.deviceId,
                  },
                })
              );
            });
          },
        },
        {
          eventName: 'OnDeviceRemoved',
          handler: (device: Device) => {
            zone.run(() => {
              store.dispatch(
                DeviceStoreActions.DeviceSignalRHubActions.deviceRemoved({
                  device: {
                    ...device,
                    lastSeen: device.lastSeen ?? undefined,
                    id: device.id ?? '',
                    deviceId: device.deviceId,
                  },
                })
              );
            });
          },
        },
        {
          eventName: 'OnDeviceSyncStatusChanged',
          handler: (status: DeviceSyncStatus) => {
            zone.run(() => {
              store.dispatch(DeviceSyncStatusActions.SignalRActions.statusChanged({ status }));
            });
          },
        },
      ],
    },

    // Location route - handles room status updates
    {
      name: 'location-group',
      urlPattern: /^\/places\/brands\/[^/]+\/locations\/[^/]+/, // (/places/brands/:brandId/locations/:locationId)
      hub: 'locations',
      groupName: async (store) => {
        const location = await lastValueFrom(store.select(LocationsRouterStoreSelectors.selectLocation).pipe(take(1)));
        if (!location?.uniqueId) return null;
        return `location_${location.uniqueId}`;
      },
      eventHandlers: [
        {
          eventName: 'OnRoomStatusEvent',
          handler: (roomStatus: RoomStatusEvent) => {
            zone.run(() => {
              store.dispatch(
                CategoryStoreActions.CategorySignalRHubActions.categoryUpdated({
                  categoryType: 'Room',
                  categoryId: roomStatus.roomId,
                  status: roomStatus.status,
                })
              );
            });
          },
        },
      ],
    },
  ];
}
